// The opponent's acceptance page: /teams/accept/:token and /teams/offer/:token. See
// kumpula/TEAMS.md.
//
// A CAPABILITY URL, and it behaves like one. No login, no account, no navigation chrome (this
// file deliberately does not import TopNavigation, which is how a route renders bare in this
// app). The token is the authorisation and it authorises exactly one thing: writing a
// confirmation. It cannot move, cancel or read the booking.
//
// IT NEVER TOUCHES FIRESTORE - it posts the token and teams-accept resolves it server-side,
// which is what lets the rules stay closed. There is no direct-read fallback anywhere here.
//
// TWO PAGES, ONE READ, ONE TOKEN. teams-accept answers with the token's own tie AND the team's
// other candidate dates in `alternatives`, so which page the opponent sees is decided by the
// PATH they were sent, not by the backend:
//
//   /teams/offer/<token>   the menu - every candidate date, pick one
//   /teams/accept/<token>  just the date this link names, which is what the manager offered
//                          (and the shape teams-utils mints into its mails)
//
// They were the same page until the manager's panel ended up printing one URL under two
// headings that promised different things. In `accept` mode the menu is still the fallback when
// the named date CANNOT be taken - claimed, expired, cancelled - because a dead link that lists
// the live dates is the reason `alternatives` exists at all.
//
// Older backends send no `alternatives`; then both modes show the single date the token names -
// see kumpula/TEAMS_BACKEND_CHANGES.md §2.
//
// THERE IS STILL NO DECLINE. Confirming is the only write; a date they do not want is one they
// never choose, and silence is 'pending', not a rejection.
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { Card, CardBody, CardHeader, Button, Alert, Spinner, Badge } from 'reactstrap'
import { TextField } from '@material-ui/core'
import withAppconfig from '../../AppContext'
import { useTeamsAccept } from '../../utils/teamsapi'
import {
  acceptOptions,
  formatDate,
  formatDateLong,
  formatDateWeekday,
  formatRubberTime,
} from '../../utils/teamsview'
import { CLAIM_COOKIE, claimFor, withClaim } from '../../utils/teamsclaim'

// The rubbers as currently booked, joined live by the backend on every call - so a court the
// venue moved this morning shows its new time here too.
function Rubbers({ rubbers, empty }) {
  const { t, i18n } = useTranslation()
  const rows = Array.isArray(rubbers) ? rubbers : []
  if (!rows.length) {
    // Links sent before the manager booked anything land here.
    return empty === false ? null : <Alert color='secondary'>{t('teams_accept_no_rubbers')}</Alert>
  }
  return (
    <div className='mb-3'>
      {rows.map((rubber, i) => (
        <div key={i} className='py-1'>
          <strong>{rubber.typelabel}</strong>{' '}
          {/* No date: every rubber belongs to the date named above it - the option's own
              heading in the menu, 'Päivämäärä' on the single-date page. */}
          {formatRubberTime(rubber.start, rubber.end, i18n.language, { withDate: false })}
          {rubber.courtname ? ' - ' + rubber.courtname : ''}
        </div>
      ))}
    </div>
  )
}

function TieSummary({ view, showDate }) {
  const { t } = useTranslation()
  return (
    <div className='mb-3'>
      {view.venue ? <div className='text-muted'>{view.venue}</div> : null}
      <h5 className='mb-1'>{view.teamname}</h5>
      {showDate && view.date ? (
        <div>
          <strong>{t('teams_accept_date')}:</strong> {formatDate(view.date)}
        </div>
      ) : null}
    </div>
  )
}

// One candidate date. Claimed ones stay listed and name the team that took it, so a date an
// opponent was offered is visibly gone rather than silently absent.
function DateOption({ option, onChoose, disabled }) {
  const { t, i18n } = useTranslation()
  return (
    <div className='border rounded p-2 mb-2'>
      <div className='d-flex justify-content-between align-items-center'>
        <strong>{formatDateLong(option.date, i18n.language)}</strong>
        {option.claimed ? (
          <Badge color='secondary'>
            {t('teams_accept_taken').replace('{OPPONENT}', option.opponentteam || '')}
          </Badge>
        ) : null}
      </div>
      <div className='small text-muted'>
        <Rubbers rubbers={option.rubbers} empty={false} />
        {!option.rubbers.length ? t('teams_accept_no_rubbers_short') : null}
      </div>
      {option.claimed ? null : (
        <Button color='primary' size='sm' disabled={disabled} onClick={() => onChoose(option)}>
          {t('teams_accept_choose_this')}
        </Button>
      )}
    </div>
  )
}

function TeamAcceptScreen({ appconfig, mode }) {
  const { t, i18n } = useTranslation()
  const { token } = useParams()
  const accept = useTeamsAccept()
  const [cookies, setCookie] = useCookies([CLAIM_COOKIE])

  const [loading, setLoading] = useState(true)
  const [view, setView] = useState(null)
  const [state, setState] = useState(null)
  const [chosen, setChosen] = useState(null)
  const [opponentteam, setOpponentteam] = useState('')
  const [saving, setSaving] = useState(false)
  const [mine, setMine] = useState(false)
  // The visitor stepped past the soft lock on purpose. Component state only, so a reload locks
  // again and every further date takes its own deliberate click.
  const [override, setOverride] = useState(false)
  const [transportFailed, setTransportFailed] = useState(false)

  const apply = (res) => {
    if (!res.ok) {
      setTransportFailed(true)
      return
    }
    setTransportFailed(false)
    // 'unknown-token' arrives BARE, with no view fields at all - so nothing here may destructure
    // them. Everything downstream reads off `view` defensively.
    setState(res.data.state || 'unknown-token')
    setView(res.data)
  }

  // The app paints <html> grey (TopNavigation does it from the venue's campaign colour, and it
  // survives client-side navigation), which would show through on overscroll behind this page.
  // The opponent sees only this card, so the page is white while it is mounted and the previous
  // value is put back on the way out.
  useEffect(() => {
    const html = document.documentElement
    const previous = html.style.backgroundColor
    html.style.backgroundColor = '#ffffff'
    return () => {
      html.style.backgroundColor = previous
    }
  }, [])

  // Omitting opponentteam is what makes this a pure read: its presence is the backend's
  // read/write discriminator, so a render must never send an empty one through as a claim.
  useEffect(() => {
    let alive = true
    setLoading(true)
    accept({ token }).then((res) => {
      if (!alive) return
      apply(res)
      setLoading(false)
    })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Claims the date the opponent picked, which is not necessarily the one the link named: each
  // option carries its own token and the write path is unchanged.
  const confirm = (useToken) => {
    if (!opponentteam.trim()) return
    setSaving(true)
    accept({ token: useToken, opponentteam }).then((res) => {
      setSaving(false)
      // THIS visitor's own claim, as opposed to finding a date already taken. Losing the race
      // answers 'already-claimed', so only a plain 'ok' counts. Without this the single-date
      // page would congratulate the opponent and then, on the very next render, decide the date
      // was gone and offer them the menu.
      const data = (res.ok && res.data) || {}
      if (data.state === 'ok' && data.confirmation && data.confirmation.state === 'confirmed') {
        setMine(true)
        // The soft lock, recorded only for a claim that actually landed: losing the race answers
        // 'already-claimed' and must not count. A season outlives any shorter window.
        setCookie(
          CLAIM_COOKIE,
          withClaim(cookies[CLAIM_COOKIE], {
            token: useToken,
            date: data.date || '',
            teamname: data.teamname || '',
          }),
          { path: '/', maxAge: 60 * 60 * 24 * 180, secure: true }
        )
      } else if (res.ok) {
        // Lost the race, or the date died meanwhile: back to the menu, which the response's own
        // alternatives now show as taken, rather than the name form for a date that is gone.
        setChosen(null)
      }
      apply(res)
    })
  }

  const options = acceptOptions(view)
  const confirmation = (view && view.confirmation) || {}
  const confirmed = confirmation.state === 'confirmed'
  // Anything but an explicit 'offer' is the single-date page: an old link, a typo in the path or
  // a missing prop must not turn a per-date link into a menu of dates the manager did not offer.
  const isOffer = mode === 'offer'
  // A date this browser has already confirmed for THIS team - matched through the team's own
  // candidate list, so every link of theirs recognises it. Per-browser and nothing more; see
  // utils/teamsclaim.js.
  const held = claimFor(cookies[CLAIM_COOKIE], view, token)
  // The date this link names cannot be had - by anyone, or by this visitor because someone else
  // already has it. In `accept` mode that is the one case worth showing the other dates for.
  // Not relaxed while a date is `chosen`: `confirmed` describes the LINK's tie, and picking
  // another date from the menu must keep the menu, not drop through to that tie's answer.
  const goneForGood =
    state === 'cancelled' ||
    state === 'expired' ||
    state === 'already-claimed' ||
    (confirmed && !mine)

  // Already holding a date for this team, in this browser. The menu and the confirm form stay out
  // of reach unless the visitor asks for them: a small division playing the same opponent twice
  // genuinely needs a second date, so the lock is a question, not a wall.
  const alreadyYours = () => (
    <>
      <TieSummary view={view} />
      <Alert color='info'>
        {t('teams_accept_already_yours').replace(
          '{DATE}',
          formatDateWeekday(held.date, i18n.language)
        )}
      </Alert>
      <div className='text-muted small'>{t('teams_accept_already_yours_help')}</div>
      <Button
        outline
        color='secondary'
        size='sm'
        className='mt-3'
        onClick={() => {
          setOverride(true)
          setChosen(null)
          setOpponentteam('')
        }}
      >
        {t('teams_accept_book_another')}
      </Button>
    </>
  )

  // Shown on every confirm path once the lock was stepped past, so a second date is never taken
  // without the first one in plain view.
  const secondWarning = () =>
    held && override ? (
      <Alert color='warning'>
        {t('teams_accept_second_warning').replace(
          '{DATE}',
          formatDateWeekday(held.date, i18n.language)
        )}
      </Alert>
    ) : null

  // The opponent's only record. teams-accept mails the HOME team's managers and nobody else -
  // the system never emails an opponent at all - so what they booked exists for them on this
  // screen and nowhere else. Hence the date, every rubber with its court, and the reminder to
  // keep the page.
  const thanks = () => (
    <>
      <TieSummary view={view} showDate />
      <Rubbers rubbers={view.rubbers} />
      {/* confirmedat comes back null even on success - a serverTimestamp is not readable inside
          its own transaction - so no timestamp is rendered here. */}
      <Alert color='success'>
        {t('teams_accept_thanks')} <strong>{confirmation.opponentteam || ''}</strong>
      </Alert>
      <Alert color='warning' className='py-2'>
        {t('teams_accept_keep_page')}
      </Alert>
      <div className='text-muted small'>{t('teams_accept_thanks_help')}</div>
    </>
  )

  const nameForm = () => (
    <>
      <TieSummary view={view} />
      {secondWarning()}
      <Alert color='info'>
        {t('teams_accept_chosen')} <strong>{formatDateLong(chosen.date, i18n.language)}</strong>
      </Alert>
      <Rubbers rubbers={chosen.rubbers} />
      <TextField
        fullWidth
        label={t('teams_accept_your_team')}
        value={opponentteam}
        onChange={(e) => setOpponentteam(e.target.value.slice(0, 120))}
      />
      <Button
        color='primary'
        className='mt-3'
        disabled={saving || !opponentteam.trim()}
        onClick={() => confirm(chosen.token)}
      >
        {saving ? <Spinner size='sm' /> : t('teams_accept_confirm')}
      </Button>
      <Button color='link' disabled={saving} onClick={() => setChosen(null)}>
        {t('teams_back')}
      </Button>
    </>
  )

  // The menu. Reached whatever the token's own tie says: a link whose date somebody else took is
  // exactly when the other options matter most, which is the point of the single link.
  const dateMenu = () => {
    const free = options.filter((option) => !option.claimed)
    return (
      <>
        <TieSummary view={view} />
        {secondWarning()}
        {/* Only a per-date link names a date that can be gone. The /offer/ link names none - its
            token is just whichever tie offerLink picked - so there it would be talking about a
            date the opponent was never offered. */}
        {!isOffer && goneForGood ? (
          <Alert color='secondary'>{t('teams_accept_this_one_gone')}</Alert>
        ) : null}
        {free.length ? (
          <div className='mb-2'>{t('teams_accept_pick_date')}</div>
        ) : (
          <Alert color='warning'>{t('teams_accept_none_left')}</Alert>
        )}
        {options.map((option) => (
          <DateOption
            key={option.token}
            option={option}
            disabled={saving}
            onChoose={(picked) => {
              setChosen(picked)
              setOpponentteam('')
            }}
          />
        ))}
        {/* Not teams_accept_choose_help: that tells the reader to open another of their
            links, which is advice for a single-date page. Here every date is already in
            front of them. */}
        <div className='text-muted small'>{t('teams_accept_menu_help')}</div>
      </>
    )
  }

  const body = () => {
    if (loading) return <Spinner color='primary' />
    if (transportFailed) return <Alert color='warning'>{t('teams_network_error')}</Alert>

    if (state === 'unknown-token') {
      return <Alert color='secondary'>{t('teams_accept_unknown')}</Alert>
    }

    // Only THIS visitor's own claim shows the thank-you. Re-opening a link whose date somebody
    // else took comes back as state 'ok' with a confirmed confirmation - 'already-claimed' is
    // what losing the race while claiming returns - so keying on the state here would greet a
    // fresh visitor with another team's confirmation instead of the menu. Nor is `chosen` enough:
    // it is set the moment a date is picked, before any claim, while `confirmed` still describes
    // the link's own tie - which thanked the visitor for another team's date.
    if (confirmed && mine) return thanks()

    // The soft lock. Below the thank-you on purpose: the visitor who just confirmed must not be
    // told off for holding the date they were thanked for a second ago.
    if (held && !override) return alreadyYours()

    // The menu. Always on /teams/offer/; on /teams/accept/ only once the date the link names is
    // beyond reach, so a per-date link keeps offering the date the manager actually chose while
    // a dead one still lists what is left.
    if (options && (isOffer || goneForGood)) {
      if (chosen) return nameForm()
      return dateMenu()
    }

    // --- fallback: a backend that does not send `alternatives` yet -----------
    if (state === 'cancelled') {
      return (
        <>
          <TieSummary view={view} showDate />
          <Alert color='secondary'>{t('teams_accept_cancelled')}</Alert>
        </>
      )
    }

    if (state === 'expired') {
      // `reason` is additive and MAY BE ABSENT - there is no sixth state to switch on.
      const reason =
        view.reason === 'spare-release'
          ? t('teams_accept_expired_spare')
          : view.reason === 'played'
          ? t('teams_accept_expired_played')
          : t('teams_accept_expired')
      return (
        <>
          <TieSummary view={view} showDate />
          <Alert color='secondary'>{reason}</Alert>
        </>
      )
    }

    // Re-opening a claimed link SHOWS the answer, never replaces it: a forwarded mail must not
    // let a second reader relabel the opponent.
    const claimedByOther = () => (
      <>
        <TieSummary view={view} showDate />
        <Rubbers rubbers={view.rubbers} />
        <Alert color='info'>
          {t('teams_accept_already')} <strong>{confirmation.opponentteam || ''}</strong>
        </Alert>
        <div className='text-muted small'>{t('teams_accept_already_help')}</div>
      </>
    )

    if (state === 'already-claimed') return claimedByOther()

    if (state === 'ok') {
      // A re-opened link reads back 'ok' + confirmed whoever claimed it; see the top of body().
      if (confirmed) return mine ? thanks() : claimedByOther()
      return (
        <>
          <TieSummary view={view} showDate />
          {secondWarning()}
          <Rubbers rubbers={view.rubbers} />
          <TextField
            fullWidth
            label={t('teams_accept_your_team')}
            value={opponentteam}
            onChange={(e) => setOpponentteam(e.target.value.slice(0, 120))}
          />
          <Button
            color='primary'
            className='mt-3'
            disabled={saving || !opponentteam.trim()}
            onClick={() => confirm(token)}
          >
            {saving ? <Spinner size='sm' /> : t('teams_accept_confirm')}
          </Button>
          <div className='text-muted small mt-2'>{t('teams_accept_choose_help')}</div>
        </>
      )
    }

    return <Alert color='secondary'>{t('teams_accept_unknown')}</Alert>
  }

  // The opponent arrives here cold, from a mailed link, with no navigation and nothing else on
  // the page - so the card is centred on plain white rather than sitting at the top of the app's
  // usual grey. `margin: auto` on the flex child centres both axes without clipping the top when
  // the card is taller than the viewport, which `align-items: center` would.
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#ffffff',
        display: 'flex',
        padding: 16,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ margin: 'auto', width: '100%', maxWidth: 560 }}>
        <Card>
          <CardHeader>{t('teams_accept_title')}</CardHeader>
          <CardBody>{body()}</CardBody>
        </Card>
      </div>
    </div>
  )
}

export default withAppconfig(TeamAcceptScreen)
